import { CashFlowTableData, CashFlowValue } from "@services/cfo/cfo.api";
import { v4 as uuid } from "uuid";

type MetricInput = number | CashFlowValue | string;

const CASH_FLOW_ORDER = [
  "total_sales",
  "cost_of_goods_sold",
  "overhead_cost",
  "gross_profit",
  "gross_margin",
  "contribution_profit",
  "contribution_margin",
  "opex",
  "net_profit",
  "net_margin",
  "roi",
  "acos",
  "tacos",
  "cash_flow",
];

const CASH_FLOW_LABELS = {
  total_sales: "Total Sales",
  cost_of_goods_sold: "Cost of Goods Sold",
  overhead_cost: "Overhead Cost",
  gross_profit: "Gross Profit",
  gross_margin: "Gross Margin",
  contribution_profit: "Contribution Profit",
  contribution_margin: "Contribution Margin",
  opex: "OPEX",
  net_profit: "Net Profit",
  net_margin: "Net Margin",
  roi: "ROI",
  acos: "ACOS",
  tacos: "TACOS",
  cash_flow: "Cash Flow",
  amazon_sales: "Amazon Sales",
  amazon_ad_sales: "Amazon Ad Sales",
  amazon_other_sales: "Amazon Other Sales",
  amazon_cogs: "Amazon COGS",
  amazon_cost: "Amazon Cost",
  amazon_fees: "Amazon Fees",
  amazon: "Amazon",
  tiktok_sales: "TikTok Sales",
  tiktok_ad_sales: "TikTok Ad Sales",
  tiktok_other_sales: "TikTok Other Sales",
  tiktok_cogs: "TikTok COGS",
  tiktok_cost: "TikTok Cost",
  tiktok_fees: "TikTok Fees",
  tiktok: "TikTok",
  shopify_sales: "Shopify Sales",
  shopify_ad_sales: "Shopify Ad Sales",
  shopify_other_sales: "Shopify Other Sales",
  shopify_cogs: "Shopify COGS",
  shopify_cost: "Shopify Cost",
  shopify_fees: "Shopify Fees",
  shopify: "Shopify",
  sponsored_brands: "Sponsored Brands",
  sponsored_display: "Sponsored Display",
  sponsored_products: "Sponsored Products",
  ad_spend: "Ad Spend",
  other_fees: "Other Fees",
  dsp_sales: "DSP Sales",
  organic_social: "Organic Social",
  other_sales: "Other Sales",
  cogs: "COGS",
  shipping_handling: "Shipping & Handling",
};

const formatCashFlowValue = (value: number, metricType: string): string => {
  if (
    metricType.includes("margin") ||
    metricType === "roi" ||
    metricType === "acos" ||
    metricType === "tacos"
  ) {
    return `${value.toFixed(1)}%`;
  }
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const getMetricValue = (metric: MetricInput): number =>
  typeof metric === "object" ? metric.value || 0 : Number(metric);

const hasComponents = (metric: MetricInput): boolean =>
  typeof metric === "object" && !!(metric as CashFlowValue).components;

const getMetricComponents = (
  metric: MetricInput,
): Record<string, unknown> | null =>
  hasComponents(metric)
    ? ((metric as CashFlowValue).components as Record<string, unknown>)
    : null;

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  return `${day} ${month}`;
};

const createChildren = (
  components: Record<string, unknown>,
  period: string,
  parentType: string,
): any[] =>
  Object.entries(components).map(([platform, rawValue]) => {
    const value = rawValue as MetricInput;
    const label =
      CASH_FLOW_LABELS[platform as keyof typeof CASH_FLOW_LABELS] ||
      platform.charAt(0).toUpperCase() + platform.slice(1).replace(/_/g, " ");
    const node: any = {
      key: uuid(),
      pnlType: label,
      metricType: platform,
      [period]: formatCashFlowValue(getMetricValue(value), parentType),
    };
    const nested = getMetricComponents(value);
    if (nested) {
      const children = createChildren(nested, period, platform);
      if (children.length) node.children = children;
    }
    return node;
  });

const processMetric = (
  metricKey: string,
  item: any,
  period: string,
  baseMetrics: Record<string, any>,
) => {
  const raw = item[metricKey as keyof typeof item] as unknown;
  const value = raw as MetricInput;
  const base = baseMetrics[metricKey];
  if (!base || value === undefined) return;
  base[period] = formatCashFlowValue(getMetricValue(value), metricKey);
  if (!hasComponents(value)) return;
  base.children ??= [];
  const comps = getMetricComponents(value);
  if (!comps) return;
  const children = createChildren(comps, period, metricKey);
  children.forEach((child) => {
    const existing = base.children.find(
      (c: any) => c.pnlType === child.pnlType,
    );
    if (existing) {
      existing[period] = child[period];
      if (child.children) {
        existing.children ??= [];
        child.children.forEach((nc: any) => {
          const existNested = existing.children.find(
            (c2: any) => c2.pnlType === nc.pnlType,
          );
          if (existNested) {
            existNested[period] = nc[period];
          } else {
            existing.children.push(nc);
          }
        });
      }
    } else {
      base.children.push(child);
    }
  });
};

export const formatTableData = (
  data: CashFlowTableData,
  dataPoints: Record<string, boolean>,
): { columns: any[]; data: any[] } => {
  const baseMetrics = CASH_FLOW_ORDER.reduce(
    (acc, key) => {
      const label =
        CASH_FLOW_LABELS[key as keyof typeof CASH_FLOW_LABELS] ||
        key
          .replace(/_/g, " ")
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
      acc[key] = { key: uuid(), pnlType: label, metricType: key };
      return acc;
    },
    {} as Record<string, any>,
  );

  const columns: any[] = [
    { title: "Type", dataIndex: "pnlType", key: "pnlType", width: 250 },
  ];

  data.forEach((item) => {
    const period = `${formatDate(item.period_start)} - ${formatDate(
      item.period_end,
    )}`;
    columns.push({ title: period, dataIndex: period, key: uuid(), width: 160 });
    CASH_FLOW_ORDER.forEach((metricKey) => {
      processMetric(metricKey, item, period, baseMetrics);
    });
  });

  const formatted = Object.values(baseMetrics)
    .filter((i: any) => dataPoints[i.metricType] !== false)
    .map((i: any) =>
      i.children && i.children.length === 0
        ? (({ children, ...rest }) => rest)(i)
        : i,
    );

  return { columns, data: formatted };
};
